import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { speakText } from './speakText';

export const PracticeStep = ({ questions, onDone }) => {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [correct, setCorrect] = useState(0);
  const q = questions[idx];

  useEffect(() => { if (q) speakText(q.question); }, [q]);

  const pick = (i) => {
    if (selected !== null) return;
    setSelected(i);
    const ok = i === q.correct_index;
    if (ok) setCorrect((c) => c + 1);
    speakText(ok ? 'Nice work!' : `The answer is ${q.options[q.correct_index]}. ${q.explanation || ''}`);
  };
  const next = () => {
    setSelected(null);
    if (idx + 1 >= questions.length) onDone(correct);
    else setIdx((i) => i + 1);
  };
  if (!q) return null;
  const isRight = selected === q.correct_index;

  return (
    <div className="rounded-3xl border-4 border-teal-200 bg-white p-6 sm:p-8" data-testid="practice-step">
      <div className="mb-4 flex items-center gap-3">
        <span className="text-sm font-medium text-slate-600">{idx + 1} / {questions.length}</span>
        <Progress value={(idx / questions.length) * 100} className="flex-1 h-3" />
      </div>
      <div className="flex items-start justify-center gap-2 mb-5">
        <h2 className="font-fredoka text-2xl font-bold text-slate-900 text-center" data-testid="practice-question">{q.question}</h2>
        <Button variant="ghost" size="icon" onClick={() => speakText(q.question)} aria-label="Read question" data-testid="practice-read-btn"><Volume2 className="w-5 h-5" /></Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {q.options.map((opt, i) => {
          const show = selected !== null;
          const cls = show && i === q.correct_index ? 'border-teal-500 bg-teal-50 text-teal-900'
            : selected === i ? 'border-red-400 bg-red-50 text-red-900' : 'border-slate-200 bg-white hover:border-teal-300 text-slate-800';
          return (
            <motion.button key={i} whileTap={!show ? { scale: 0.96 } : {}} onClick={() => pick(i)} disabled={show}
              className={`p-4 rounded-2xl text-lg font-semibold border-2 min-h-[64px] transition-colors ${cls}`} data-testid={`practice-option-${i}`}>
              {opt}
            </motion.button>
          );
        })}
      </div>
      {selected !== null && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-5 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-slate-700 flex items-center gap-2" data-testid="practice-feedback">
            {isRight ? <Check className="w-5 h-5 text-teal-600" /> : <X className="w-5 h-5 text-red-500" />}
            {isRight ? 'Nice work!' : `Answer: ${q.options[q.correct_index]}`}
          </p>
          <Button onClick={next} className="bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="practice-next-btn">
            {idx + 1 >= questions.length ? 'Done' : 'Next'}
          </Button>
        </motion.div>
      )}
    </div>
  );
};

export default PracticeStep;
